import { useEffect, useId, useState, type FormEvent } from 'react';
import { Check, X } from 'lucide-react';
import { formatDate } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, IconButton, Select, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { requestDocuments, type ChecklistView, type FinancierRecordFields } from './api';
import { addDaysKey, todayKey } from './form';
import styles from './financier.module.css';

/**
 * Finanzierer 04 „Unterlagen anfordern" (`POST /financings/:id/document-requests`, channel e-mail — the client
 * portal of the frame is M7): the requestable customer items (missing ones preselected), free extra titles,
 * „Benötigt bis", „Kopie an" (default the advisor in charge).
 */

export interface RequestDocumentsDialogProps {
  open: boolean;
  financing: FinancierRecordFields;
  requestable: ChecklistView['requestable'];
  onClose: () => void;
  onDone: () => void;
}

const NO_COPY = 'none';

interface ExtraItem {
  key: number;
  title: string;
}

export function RequestDocumentsDialog({
  open,
  financing,
  requestable,
  onClose,
  onDone,
}: RequestDocumentsDialogProps) {
  const formId = useId();
  const name = financing.linked.contact.name;
  const advisor = financing.advisor;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [extras, setExtras] = useState<ExtraItem[]>([]);
  const [neededByOn, setNeededByOn] = useState('');
  const [copyTo, setCopyTo] = useState<string>(NO_COPY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(requestable.filter((item) => item.preselected).map((item) => item.documentTypeId)));
    setExtras([]);
    setNeededByOn(addDaysKey(todayKey(), 3));
    setCopyTo(advisor?.id ?? NO_COPY);
    setErrors({});
    setBanner(null);
  }, [open]);

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const copyName = copyTo !== NO_COPY && advisor ? advisor.name : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const items = [
      ...requestable
        .filter((item) => selected.has(item.documentTypeId))
        .map((item) => ({ documentTypeId: item.documentTypeId, title: item.title })),
      ...extras.filter((item) => item.title.trim() !== '').map((item) => ({ title: item.title.trim() })),
    ];
    const next: Record<string, string> = {};
    if (items.length === 0) next['items'] = 'Bitte wählen Sie mindestens eine Unterlage.';
    if (!neededByOn) next['neededByOn'] = 'Pflichtangabe.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      await requestDocuments(financing.id, {
        items,
        neededByOn,
        channel: 'email',
        copyToId: copyTo === NO_COPY ? null : copyTo,
      });
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setErrors(error.fields);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const copyOptions = [
    ...(advisor ? [{ value: advisor.id, label: `${advisor.name} · Berater` }] : []),
    { value: NO_COPY, label: 'Keine Kopie' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Unterlagen anfordern"
      title={`Was fehlt noch von ${name}?`}
      subtitle={`${name} erhält die Anforderung per E-Mail.${copyName ? ` ${copyName} bekommt eine Kopie.` : ''}`}
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <fieldset className={styles.options} style={{ border: 0, margin: 0, padding: 0 }}>
          <legend className={styles.srOnly}>Unterlagen</legend>
          {requestable.map((item) => {
            const active = selected.has(item.documentTypeId);
            return (
              <label key={item.documentTypeId} className={clsx(styles.option, active && styles.optionActive)}>
                <input
                  type="checkbox"
                  className={styles.optionInput}
                  checked={active}
                  onChange={() => toggle(item.documentTypeId)}
                />
                <span className={styles.optionBox} aria-hidden="true">
                  {active ? <Check size={12} strokeWidth={2.5} aria-hidden="true" /> : null}
                </span>
                <span className={styles.optionText}>
                  <span className={styles.optionTitle}>{item.title}</span>
                  {item.sub ? <span className={styles.optionSub}>{item.sub}</span> : null}
                </span>
              </label>
            );
          })}
          {extras.map((item, index) => (
            <div key={item.key} className={styles.extraRow}>
              <TextField
                label={`Weitere Unterlage ${index + 1}`}
                labelHidden
                placeholder="Titel der Unterlage"
                value={item.title}
                maxLength={200}
                autoFocus
                onChange={(event) =>
                  setExtras((current) =>
                    current.map((entry) =>
                      entry.key === item.key ? { ...entry, title: event.target.value } : entry,
                    ),
                  )
                }
              />
              <IconButton
                label="Entfernen"
                icon={X}
                variant="ghost"
                onClick={() => setExtras((current) => current.filter((entry) => entry.key !== item.key))}
              />
            </div>
          ))}
          <span>
            <button
              type="button"
              className={styles.link}
              onClick={() => setExtras((current) => [...current, { key: Date.now(), title: '' }])}
            >
              Weitere Unterlage hinzufügen
            </button>
          </span>
          {errors['items'] ? (
            <span className={styles.fieldError} role="alert">
              {errors['items']}
            </span>
          ) : null}
        </fieldset>
        <div className={styles.dialogGrid}>
          <TextField
            label="Benötigt bis"
            type="date"
            value={neededByOn}
            min={todayKey()}
            onChange={(event) => setNeededByOn(event.target.value)}
            error={errors['neededByOn']}
          />
          <Select
            label="Kopie an"
            options={copyOptions}
            value={copyTo}
            onChange={(event) => setCopyTo(event.target.value)}
            error={errors['copyToId']}
          />
        </div>
        <div className={styles.infoBlock}>
          <span>{name} erhält eine E-Mail mit der Liste der Unterlagen.</span>
          {neededByOn && advisor ? (
            <span>
              Kommt bis {formatDate(`${neededByOn}T12:00:00Z`)} nichts, erhält {advisor.name} eine
              Erinnerungs-Task.
            </span>
          ) : null}
        </div>
        <div className={styles.cardFooterPlain} style={{ marginTop: 6 }}>
          <span className={styles.footActions}>
            <Button variant="secondary" onClick={onClose} disabled={pending}>
              Abbrechen
            </Button>
            <Button type="submit" loading={pending}>
              Anfordern
            </Button>
          </span>
        </div>
      </form>
    </Dialog>
  );
}
